import { ColumnOptions } from '@/interfaces/ColumnOptions';
import { Column } from '@/interfaces/Column';
import moment from 'moment';
import router from '@/router/index';
import { RouteNames } from '@/enums/routes/RouteNames';
import { AppTableColumn } from '@/enums/components/AppTableColumn';
import { ProjectTypes } from '@/enums/models/ProjectTypes';
import { ColumnSortedInfo } from '@/interfaces/ColumnSortedInfo';
import { LocalStorageService } from '@/services/LocalStorageService';
import i18n from '@/i18n';
import { sortDates, sortStrings } from '@/helpers/SortingHelper';
import { constructUrlParams } from '@/helpers/SearchHelper';
import NextStep from '@/models/NextStep';
import Project from '@/models/Project';
import { FetchTableEntitiesConfiguration } from '@/interfaces/components/ProjectTable/FetchTableEntitiesConfiguration';
import { PrintTableConfigurator } from '@/interfaces/components/ProjectTable/PrintTableConfigurator';
import { FilterUrl } from '@/interfaces/general/FilterUrl';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import { TablesArrayConfiguration } from '@/interfaces/general/TablesArrayConfiguration';
import { ProjectTabValues } from '@/enums/components/Project/ProjectTabValues';
import Offer from '@/models/Offer';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { notification } from 'ant-design-vue';
import Vue from 'vue';
import { generateStateSpecificId, extractIdOfNewestOffer } from '@/helpers/project/ProjectHelper';
import { OfferStates } from '@/enums/global/OfferStates';
import { ClientTabs } from '@/enums/components/EditClient/ClientTabs';
import { ProjectStates } from '@/enums/components/Project/ProjectStates';
import { getSelectedOfferPrice } from '@/helpers/NewProject/NewProjectHelper';
import { formatNumberForDisplay } from '@/helpers/NumberFormatter';
import { phoneIcon } from '@/icons/PhoneIcon';
import { enviromentIcon } from '@/icons/EnviromentIcon';
import ProjectSearch from '@/models/ProjectSearch';
import { downloadExcelFile, downloadTableAsExcel, ExportColumn } from '@/helpers/PrintHelper';
import store from '@/store';

export function createColumnData<T extends { [key: string]: any }>(
    columnOptions: ColumnOptions[],
    sortedInfo: ColumnSortedInfo | null,
    currentEntityType: string,
    callback?: (record?: any) => Promise<void>,
    navigation: boolean = true,
    userGroup: string | null = null,
    isCurrentUserTechnologist: boolean = false,
): Array<Column<T>> {
    const columnData: Array<Column<T>> = [];
    const datePickerLocale = getDatepickerLocale();

    columnOptions.forEach((columnOption: ColumnOptions) => {
        if (currentEntityType === ProjectTypes.Request) {
            columnData.push(
                formatTableValuesForRequestTab(
                    columnOption,
                    sortedInfo,
                    currentEntityType,
                    datePickerLocale,
                    navigation,
                    userGroup,
                    callback,
                ),
            );
        } else {
            columnData.push(
                formatTableValuesForAllTabs(
                    columnOption,
                    sortedInfo,
                    currentEntityType,
                    datePickerLocale,
                    navigation,
                    userGroup,
                    callback,
                    isCurrentUserTechnologist,
                ),
            );
        }
    });

    return columnData;
}

function formatTableValuesForRequestTab<T extends { [key: string]: any }>(
    columnOption: ColumnOptions,
    sortedInfo: ColumnSortedInfo | null,
    currentEntityType: string,
    datePickerLocale: any,
    navigation: boolean = true,
    userGroup: string | null,
    callback?: (record?: any) => Promise<void>,
): Column<T> {
    const strippedKey = columnOption.shouldStripNumbers ? columnOption.key.replace(/\d+/, '') : columnOption.key;
    const columns: Column<T> = {
        title: i18n.t(columnOption.name) as string,
        dataIndex: columnOption.key,
        key: columnOption.key,
        sortOrder: sortedInfo && sortedInfo.columnKey === columnOption.key ? sortedInfo.order : null,
        customCell(record: T): object {
            let renderText: null | string = record[strippedKey];

            // Add fallback to createdAt for specific date fields when they're not present
            if ((strippedKey === 'offerCreatedAt' || strippedKey === 'orderCreatedAt') && !renderText) {
                renderText = record['createdAt'];
            }

            let color: string | null = '';
            if (strippedKey === 'status') {
                const projectHasActiveOrderStatus = record.orderStatus != null;
                if (projectHasActiveOrderStatus) {
                    renderText = record.orderStatus.name;
                } else {
                    renderText = record.label ? record.label.name : '-';
                }

                if (record.state === 'request') {
                    renderText = i18n.t('Roltek kontrola') as string;
                }

                if (projectHasActiveOrderStatus) {
                    color = null;
                }

                if (record.label) {
                    color = record.label.color;
                }
            }

            if (strippedKey === 'orderNumber') {
                const orderedOffer = record.offerRevisions.filter(
                    (offer: Offer) => offer.state === OfferStates.ORDERED,
                );

                renderText = record.orderNumber != null ? record.orderNumber : orderedOffer[0]?.name ?? '';
            }

            if (strippedKey === 'typeSpecificId') {
                renderText = generateStateSpecificId(record as unknown as Project);
            }
            if (strippedKey === 'offerRevisions.name') {
                renderText = extractIdOfNewestOffer(record as unknown as Project);
            }

            if (strippedKey === 'dateCreated') {
                switch (record.state) {
                    case 'offer':
                        const sortedOffers = sortArrayByGivenPropertyName(
                            record.offerRevisions.slice(),
                            columnOption.sortBy,
                        );
                        renderText = sortedOffers.splice(-1)[0].createdAt;
                        break;
                    case 'order':
                        const orderedOffer = record.offerRevisions.filter(
                            (offer: Offer) => offer.state === OfferStates.ORDERED,
                        );
                        const importedOffer = record.offerRevisions.filter(
                            (offer: Offer) => offer.state === OfferStates.IMPORTED,
                        );

                        const offerToBeUsed = orderedOffer.length > 0 ? orderedOffer : importedOffer;

                        renderText = offerToBeUsed.length > 0 ? offerToBeUsed[0].createdAt : null;
                        break;
                    default:
                        renderText = record.sortDate;
                }
            }

            if (strippedKey === 'manufactureDate') {
                if (record.manufactureDate && record.manufactureDate !== 'null') {
                    renderText = record.manufactureDate;
                } else {
                    renderText = '';
                }
            }

            if (strippedKey === 'sellingOfferPrice.priceWithTax') {
                let priceValue = getSelectedOfferPrice(record?.id);
                if (!priceValue || priceValue === '-') {
                    priceValue = record?.offerRevisions[0]?.sellingOfferPrice?.priceWithTax;
                }
                if (priceValue !== undefined) {
                    renderText = formatNumberForDisplay(priceValue as any as number, 2, 2);
                } else {
                    renderText = '-';
                }
            }

            if (columnOption.type.includes('nested')) {
                const keys = strippedKey.split('.');
                let value = record;
                for (const key of keys) {
                    if (value == null) {
                        renderText = null;
                        break;
                    }
                    value = value[key];
                }
                renderText = value !== null ? (value as unknown as string) : '';
                if (columnOption.type.includes('contact')) {
                    // add additional personal contact information
                    renderText = `<span style="color: black;">` + renderText + `</span>`;
                    renderText += '<br/><span class="client-information">';
                    let phoneValue: string = '';
                    switch (strippedKey) {
                        case 'name':
                            phoneValue = record?.contactPersons[0]?.phone;
                            break;
                        case 'project.client.name':
                            phoneValue = record?.project?.contactPerson?.phone;
                            break;
                        default:
                            phoneValue = record?.contactPerson?.phone ?? record?.client?.contactPersons[0]?.phone;
                            break;
                    }
                    const isTablePrinting = store.getters['projectLoadingState/isTablePrinting'];
                    if (phoneValue && phoneValue !== '') {
                        renderText += isTablePrinting ? `<span style="white-space: nowrap;">` : '';
                        renderText +=
                            `<a class="print-client-phone" href=tel:"${phoneValue}"><span>${phoneIcon}</span> ` +
                            (phoneValue as unknown as string) +
                            `</a>`;
                        if (isTablePrinting && record?.clientEmail) {
                            renderText += ` <span class="client-email">${record.clientEmail}</span>`;
                        }
                        renderText += isTablePrinting ? `</span>` : '';
                    } else if (isTablePrinting && record?.clientEmail) {
                        renderText += `<span class="client-email" style="white-space: nowrap;">${record.clientEmail}</span>`;
                    }
                    let addressStreetValue = '';
                    let addressPostValue = '';
                    let addressCityValue = '';
                    switch (strippedKey) {
                        case 'project.client.name':
                            addressStreetValue = record?.project?.client?.address?.streetNameAndNumber;
                            addressPostValue = record?.project?.client?.address?.poNumber;
                            addressCityValue = record?.project?.client?.address?.city;
                            break;
                        default:
                            addressStreetValue = record?.client?.address?.streetNameAndNumber;
                            addressPostValue = record?.client?.address?.poNumber;
                            addressCityValue = record?.client?.address?.city;
                            break;
                    }
                    const isAndroid = /Android/i.test(navigator.userAgent);
                    if (isAndroid) {
                        const geoLoaction = encodeURIComponent(
                            'geo:0,0?q=' + addressStreetValue + '+' + addressCityValue,
                        );
                        renderText += `<a href="${geoLoaction}">`;
                    }
                    if (addressStreetValue && addressStreetValue !== null && addressStreetValue !== '') {
                        renderText += `<span>${enviromentIcon}</span>` + ' ' + addressStreetValue + ',';
                    }

                    if (addressPostValue && addressPostValue !== null) {
                        renderText += ' ' + addressPostValue;
                    }

                    if (addressCityValue && addressCityValue !== null) {
                        renderText += ' ' + addressCityValue;
                    }
                    renderText += isAndroid ? `</a>` : '';
                    renderText += '</span>';
                }

                if (columnOption.badge) {
                    keys.splice(-1, 1, 'color');
                    let colorValue = record;
                    for (const key of keys) {
                        if (colorValue == null) {
                            color = null;
                            break;
                        }
                        colorValue = colorValue[key];
                    }
                    color = colorValue as unknown as string;
                }
            }
            if (columnOption.type.includes('datetime')) {
                renderText =
                    (renderText ? moment(renderText).format(datePickerLocale.lang.dateFormat) : '-') +
                    ' ' +
                    (renderText ? moment(renderText).format('HH:mm:ss') : '-');
            } else if (columnOption.type.includes('date')) {
                renderText = renderText ? moment(renderText).format(datePickerLocale.lang.dateFormat) : '-';
            } else if (columnOption.type.includes('time')) {
                renderText = renderText ? moment(renderText).format('HH:mm:ss') : '-';
            }
            if (columnOption.type.includes('array')) {
                const keys = strippedKey.split('.');
                const lastKey = keys.splice(-1)[0];
                let values = record;

                for (const key of keys) {
                    if (values == null) {
                        renderText = null;
                        break;
                    }
                    values = values[key];
                }

                values = filterArrayIfNecessary(values as unknown as any[], columnOption.key) as unknown as T;

                if (values == null || values.length <= 0) {
                    renderText = null;
                } else {
                    renderText = handleArraysDisplayValue({
                        array: (values as unknown as any[]).slice(),
                        columnOption,
                        propertyToBeDisplayed: lastKey,
                        dateFormat: datePickerLocale.lang.dateFormat,
                        projectState: record.state,
                    });
                }
            }
            if (renderText == null) {
                renderText = '-';
            }

            let badgeText = renderText;

            if (record.orderStatus != null) {
                badgeText = record.orderStatus.name;
            }
            if (record.state === ProjectStates.REQUEST) {
                badgeText = i18n.t('Roltek kontrola') as string;
            }

            return {
                class: `${columnOption.badge ? 'has-badge' : ''} ${columnOption.isNarrowWidth ? 'is-narrow' : ''} ${
                    columnOption.shouldBreakNone ? 'break-none' : ''
                } ${columnOption.ellipsis ? 'has-ellipsis' : ''} ${columnOption.shouldBreakAll ? 'break-all' : ''}`,
                on: {
                    click: async () => {
                        if (currentEntityType !== 'clients') {
                            LocalStorageService.save('lastSelectedTab', currentEntityType);
                            const initialTab = determineInitialTab(record.state);

                            if (callback != null) {
                                await callback(record);
                            }
                            if (navigation) {
                                router.push({
                                    name: RouteNames.project,
                                    params: {
                                        id: record.id,
                                        tab: 'request',
                                    },
                                    query: {
                                        initialTab: initialTab,
                                        ug: userGroup,
                                    },
                                });
                            }
                        } else if (navigation) {
                            router.push({
                                name: RouteNames.editClient,
                                params: {
                                    clientId: record.id,
                                },
                                query: { initialTab: String(ClientTabs.CUSTOMER) },
                            });
                        }
                    },
                },
                domProps: {
                    innerHTML: columnOption.badge
                        ? `<button class="u-b3 status-switcher ant-btn ant-dropdown-trigger"
                        style="background-color:${record.state === 'request' ? null : `${color}20`};
                            white-space: nowrap;
                            border-color:${record.state === 'request' ? null : color};
                            color:${record.state === 'request' ? null : color}">${badgeText}
                        </button>`
                        : `<span>${renderText}</span>`,
                },
            };
        },
    };
    if (columnOption.shouldSort) {
        columns.sorter = true;
    }
    if (columnOption.shouldFilter) {
        columns.scopedSlots = {
            filterDropdown: 'filterDropdown',
            filterIcon: 'filterIcon',
        };
    }

    if (columnOption.type.includes('editable')) {
        columns.customCell = undefined;
        columns.scopedSlots = {
            customRender: columnOption.slotName,
        };
    }

    return columns;
}

function determineInitialTab(tab: string) {
    switch (tab) {
        case ProjectStates.LEAD:
            return '0';
        case ProjectStates.OFFER:
            return '2';
        case ProjectStates.ORDER:
            return '3';
        default:
            return '2';
    }
}

function formatTableValuesForAllTabs<T extends { [key: string]: any }>(
    columnOption: ColumnOptions,
    sortedInfo: ColumnSortedInfo | null,
    currentEntityType: string,
    datePickerLocale: any,
    navigation: boolean = true,
    userGroup: string | null,
    callback?: (record?: any) => Promise<void>,
    isCurrentUserTechnologist: boolean = false,
): Column<T> {
    const strippedKey = columnOption.shouldStripNumbers ? columnOption.key.replace(/\d+/, '') : columnOption.key;
    const columns: Column<T> = {
        title: i18n.t(columnOption.name) as string,
        dataIndex: columnOption.key,
        key: columnOption.key,
        sortOrder: sortedInfo && sortedInfo.columnKey === columnOption.key ? sortedInfo.order : null,
        customCell(record: T): object {
            let renderText: null | string = record[strippedKey];

            // Add fallback to createdAt for specific date fields when they're not present
            if ((strippedKey === 'offerCreatedAt' || strippedKey === 'orderCreatedAt') && !renderText) {
                renderText = record['createdAt'];
            }

            let color: string | null = '';

            if (strippedKey === 'typeSpecificId') {
                renderText = record?.typeSpecificId || record?.id || '-';
            }

            if (strippedKey === 'orderNumber') {
                renderText = record?.orderNumber || record?.id || '-';
            }

            if (isCurrentUserTechnologist && strippedKey === 'price') {
            }

            if (strippedKey === 'price') {
                if (record.price && !isCurrentUserTechnologist) {
                    renderText = formatNumberForDisplay(record.price as any as number, 2, 2);
                } else {
                    renderText = '-';
                }
            }

            if (strippedKey === 'connection') {
                if (record?.connection) {
                    renderText = record.connection;
                } else {
                    renderText = '-';
                }
            }

            if (columnOption.type.includes('nested')) {
                const keys = strippedKey.split('.');
                let value = record;
                for (const key of keys) {
                    if (value == null) {
                        renderText = null;
                        break;
                    }
                    value = value[key];
                }
                renderText = value !== null ? (value as unknown as string) : '';
                if (columnOption.type.includes('contact')) {
                    // add additional personal contact information
                    renderText = `<span style="color: black;">` + renderText + `</span>`;
                    renderText += '<br/><span class="client-information">';
                    let phoneValue: string = record?.contactPersonPhone;
                    const isTablePrinting = store.getters['projectLoadingState/isTablePrinting'];

                    if (phoneValue) {
                        renderText += isTablePrinting ? `<span style="white-space: nowrap;">` : '';
                        renderText +=
                            `<a class="print-client-phone" href=tel:"${phoneValue}"><span>${phoneIcon}</span> ` +
                            (phoneValue as unknown as string) +
                            `</a>`;
                        if (isTablePrinting && record?.clientEmail) {
                            renderText += ` <span class="client-email">${record.clientEmail}</span>`;
                        }
                        renderText += isTablePrinting ? `</span>` : '';
                    } else if (isTablePrinting && record?.clientEmail) {
                        renderText += `<span class="client-email" style="white-space: nowrap;">${record.clientEmail}</span>`;
                    }

                    let addressStreetValue = record?.clientStreetNameAndNumber;
                    let addressPostValue = record?.clientPoNumber ?? '';
                    let addressCityValue = record?.clientCity ?? '';

                    const isAndroid = /Android/i.test(navigator.userAgent);
                    if (isAndroid) {
                        const geoLoaction = encodeURIComponent(
                            'geo:0,0?q=' + addressStreetValue + '+' + addressCityValue,
                        );
                        renderText += `<a href="${geoLoaction}">`;
                    }
                    if (addressStreetValue && addressStreetValue !== null && addressStreetValue !== '') {
                        renderText += `<span>${enviromentIcon}</span>` + ' ' + addressStreetValue + ',';
                    }

                    if (addressPostValue && addressPostValue !== null) {
                        renderText += ' ' + addressPostValue;
                    }

                    if (addressCityValue && addressCityValue !== null) {
                        renderText += ' ' + addressCityValue;
                    }
                    renderText += isAndroid ? `</a>` : '';
                    renderText += '</span>';
                }

                if (columnOption.badge) {
                    keys.splice(-1, 1, 'color');
                    let colorValue = record;
                    for (const key of keys) {
                        if (colorValue == null) {
                            color = null;
                            break;
                        }
                        colorValue = colorValue[key];
                    }
                    color = colorValue as unknown as string;
                }
            }

            if (columnOption.type.includes('datetime')) {
                renderText =
                    (renderText ? moment(renderText).format(datePickerLocale.lang.dateFormat) : '-') +
                    ' ' +
                    (renderText ? moment(renderText).format('HH:mm:ss') : '-');
            } else if (columnOption.type.includes('date')) {
                renderText = renderText ? moment(renderText).format(datePickerLocale.lang.dateFormat) : '-';
            } else if (columnOption.type.includes('time')) {
                renderText = renderText ? moment(renderText).format('HH:mm:ss') : '-';
            }
            if (columnOption.type.includes('array')) {
                const keys = strippedKey.split('.');
                const lastKey = keys.splice(-1)[0];
                let values = record;

                for (const key of keys) {
                    if (values == null) {
                        renderText = null;
                        break;
                    }
                    values = values[key];
                }

                values = filterArrayIfNecessary(values as unknown as any[], columnOption.key) as unknown as T;

                if (values == null || values.length <= 0) {
                    renderText = null;
                } else {
                    renderText = handleArraysDisplayValue({
                        array: (values as unknown as any[]).slice(),
                        columnOption,
                        propertyToBeDisplayed: lastKey,
                        dateFormat: datePickerLocale.lang.dateFormat,
                        projectState: record.state,
                    });
                }
            }
            if (renderText == null) {
                renderText = '-';
            }

            return {
                class: `
                    ${columnOption.isNarrowWidth ? 'is-narrow' : ''} 
                    ${columnOption.shouldBreakNone ? 'break-none' : ''} 
                    ${columnOption.ellipsis ? 'has-ellipsis' : ''} 
                    ${columnOption.shouldBreakAll ? 'break-all' : ''}
                    ${record?.isBlocked || record?.isClientBlocked ? 'low-opacity' : ''}
                `,
                on: {
                    click: async () => {
                        if (currentEntityType !== 'clients') {
                            LocalStorageService.save('lastSelectedTab', currentEntityType);
                            const initialTab = determineInitialTab(record.state);

                            if (callback != null) {
                                await callback(record);
                            }
                            if (navigation) {
                                router.push({
                                    name: RouteNames.project,
                                    params: {
                                        id: record.id,
                                    },
                                    query: {
                                        initialTab: initialTab,
                                        ug: userGroup,
                                    },
                                });
                            }
                        } else if (navigation) {
                            router.push({
                                name: RouteNames.editClient,
                                params: {
                                    clientId: record.id,
                                },
                                query: { initialTab: String(ClientTabs.CUSTOMER) },
                            });
                        }
                    },
                },
                domProps: {
                    innerHTML: `<span>${renderText}</span>`,
                },
            };
        },
    };
    if (columnOption.shouldSort) {
        columns.sorter = true;
    }
    if (columnOption.shouldFilter) {
        columns.scopedSlots = {
            filterDropdown: 'filterDropdown',
            filterIcon: 'filterIcon',
        };
    }

    if (columnOption.type.includes('editable')) {
        columns.customCell = undefined;
        columns.scopedSlots = {
            customRender: columnOption.slotName,
        };
    }

    return columns;
}

export function sortArrayByGivenPropertyName(array: any[], propertyName: string | undefined) {
    let sortedArray = array;
    switch (propertyName) {
        case 'updatedAt':
        case 'createdAt':
        case 'dateFrom':
        case 'dateTo':
        case 'date':
            sortedArray = array.sort((valueA: any, valueB: any) => {
                if (propertyName == null) {
                    return -1;
                }

                return sortDates(valueA[propertyName], valueB[propertyName], true);
            });
            break;
        default:
            sortedArray = array.sort((valueA: any, valueB: any) => {
                if (propertyName == null) {
                    return -1;
                }

                return sortStrings(valueA[propertyName], valueB[propertyName], true);
            });
    }
    return sortedArray;
}

function handleArraysDisplayValue({
    array,
    columnOption,
    propertyToBeDisplayed,
    dateFormat,
    projectState,
}: TablesArrayConfiguration) {
    let valueToBeDisplayed: string | null;

    if (typeContainsPositioningKeyword(columnOption.type)) {
        const sortedArray = sortArrayByGivenPropertyName(array, columnOption.sortBy);

        valueToBeDisplayed = getCorrectElementFromArray(sortedArray, columnOption.type, propertyToBeDisplayed);
        valueToBeDisplayed = convertValueToDateIfNecessary(valueToBeDisplayed, columnOption.type, dateFormat);
    } else if (shouldDisplayValueBeLength(columnOption.type)) {
        valueToBeDisplayed = String(array.length);
    } else if (propertyToBeDisplayed === 'createdAt' && (projectState === 'order' || projectState === 'ordered')) {
        valueToBeDisplayed = extractCreatedAtDateFromOrderedOffer(
            array,
            columnOption.type,
            dateFormat,
            propertyToBeDisplayed,
        );
    } else {
        const arrayOfPropertiesToBeDisplayed = array.map(
            (valueObject: any[]) => valueObject[propertyToBeDisplayed as any],
        );

        valueToBeDisplayed = arrayOfPropertiesToBeDisplayed.join(', ');
    }

    return valueToBeDisplayed;
}

function shouldDisplayValueBeLength(columnOptionType: string) {
    return columnOptionType.includes('length');
}

function convertValueToDateIfNecessary(value: string | null, columnOptionType: string, dateFormat = 'DD.MM.YYYY') {
    if (!columnOptionType.includes('date') || value == null) {
        return value;
    }

    return moment(value).format(dateFormat);
}

function getCorrectElementFromArray(array: any[], columnOptionType: string, propertyNameToGet: string) {
    return columnOptionType.includes('last') ? array.splice(-1)[0][propertyNameToGet] : array[0][propertyNameToGet];
}

function filterArrayIfNecessary(array: any[], columnOptionKey: string) {
    if (!columnOptionKey.includes('nextSteps') || array == null) {
        return array;
    }

    return array.filter((value: NextStep) => !value.completed);
}

function typeContainsPositioningKeyword(columnOptionType: string) {
    return columnOptionType.includes('last') || columnOptionType.includes('first');
}

export function constructSearchParametersFromLocalStorage(
    entityType: string,
    currentUserId: string,
    results: number | null = null,
) {
    let localStorageParameters = JSON.parse(
        LocalStorageService.get(
            // @ts-ignore
            `${entityType}-${currentUserId}`,
        ) as string,
    );
    if (localStorageParameters == null) {
        throw new Error('Local storage parameters do not exist');
    }
    const currentPageNumber = localStorageParameters.page;

    localStorageParameters = updatePageAndResultsNumberOnSearchParameters(localStorageParameters, results);

    return { query: constructUrlParams(localStorageParameters), localStorageParameters, currentPageNumber };
}

function updatePageAndResultsNumberOnSearchParameters(searchParameters: FilterUrl, results: number | null) {
    searchParameters.page = 1;
    searchParameters.results = results || 1000;

    return searchParameters;
}

export async function fetchActivitiesOrNextStepsBasedOnParameters({
    parameters,
    query,
}: FetchTableEntitiesConfiguration) {
    try {
        if (parameters.entityType === ProjectTypes.Request) {
            await Project.getFilteredProjects(query, parameters.entityType);
        } else {
            await ProjectSearch.getAll(query);
        }
    } catch (e) {
        throw e;
    }

    return Promise.resolve();
}

function waitForRender(callback: () => void) {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
}

export async function prepareAndPrintTable(
    entityType: string,
    currentUserId: string,
    printFunction: any,
    fromFilter?: string,
) {
    let query!: string;
    let localStorageParameters!: FilterUrl;
    let currentPageNumber!: number;

    try {
        const searchParameters = constructSearchParametersFromLocalStorage(entityType, currentUserId);
        query = searchParameters.query;

        if (fromFilter) {
            query += '&includeAdditional=' + fromFilter;
        }

        localStorageParameters = searchParameters.localStorageParameters;
        currentPageNumber = searchParameters.currentPageNumber;

        await fetchActivitiesOrNextStepsBasedOnParameters({
            parameters: localStorageParameters,
            query,
        });
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        return Promise.reject();
    }

    EventBus.$emit(EventBusEvents.emitProjectPagination, {
        currentPage: localStorageParameters.page,
        pageSize: localStorageParameters.results,
    });

    await Vue.nextTick();
    // popups in the browser must be allowed in some cases where the table has a lot of data
    // for this to work
    waitForRender(() => {
        printTable({
            printFunction,
            elementId: 'tableWrapper',
            entityType,
            onPrintFinish: () => {
                EventBus.$emit(EventBusEvents.runEntitySearch, {
                    page: currentPageNumber,
                });
                store.dispatch('projectLoadingState/updateIsTablePrinting', false);
                return Promise.resolve();
            },
        });
    });
}

export async function prepareAndPrintExtendedTable(
    entityType: string,
    currentUserId: string,
    fromFilter?: string,
    pageSize?: number,
) {
    let query!: string;

    try {
        const searchParameters = constructSearchParametersFromLocalStorage(entityType, currentUserId);
        query = searchParameters.query;

        query += '&includeAdditional=' + (fromFilter ?? 'all');

        if (pageSize) {
            query += '&page[size]=' + pageSize;
        }

        const projects = await ProjectSearch.getForExtendedPrint(query);

        if (!projects?.length) {
            notification.info({
                message: i18n.t('No data for printing') as string,
                description: '',
            });
            return Promise.resolve();
        }

        await Vue.nextTick();

        prepareDataForExtendedPrintAndPrint({
            projects,
            entityType,
        });

        return Promise.resolve();
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        return Promise.reject();
    }
}

function prepareDataForExtendedPrintAndPrint({ projects, entityType }: { projects: any[]; entityType: string }) {
    if (!projects || projects.length === 0) {
        return;
    }

    // First, determine the maximum number of internal orders and logs across all projects
    let maxInternalOrders = 0;
    let maxLogs = 0;

    for (const project of projects) {
        const ordersCount = project.projectInternalOrders?.length || 0;
        const logsCount =
            (project.projectLogs || []).filter((log: any) => log.changeType === 'STATUS_CHANGE').length || 0;
        if (ordersCount > maxInternalOrders) maxInternalOrders = ordersCount;
        if (logsCount > maxLogs) maxLogs = logsCount;
    }

    // Define base columns for projects
    const projectColumns: ExportColumn[] = [
        { title: String(i18n.t('Id')), dataIndex: 'typeSpecificId' },
        { title: String(i18n.t('Klijent')), dataIndex: 'client' },
        { title: String(i18n.t('Datum izrade')), dataIndex: 'createdAt' },
        { title: String(i18n.t('Status')), dataIndex: 'state' },
        { title: String(i18n.t('Komercijalist')), dataIndex: 'assignedUserName' },
        { title: String(i18n.t('Cijena')), dataIndex: 'price' },
        { title: String(i18n.t('Projekt-veza')), dataIndex: 'connection' },
    ];

    // Build dynamic columns array
    const combinedColumns: ExportColumn[] = [...projectColumns];

    // Add internal order columns - first one gets the header "Internal Orders", rest are empty titles
    for (let i = 0; i < maxInternalOrders; i++) {
        combinedColumns.push({
            title: i === 0 ? String(i18n.t('Internal orders')) : '',
            dataIndex: `internalOrder_${i}`,
        });
    }

    // Add log columns - each log has two columns: "Log {n}" for message, empty for date
    for (let i = 0; i < maxLogs; i++) {
        combinedColumns.push({
            title: `${String(i18n.t('Log'))} ${i + 1}`,
            dataIndex: `log_${i}_message`,
        });
        combinedColumns.push({
            title: '',
            dataIndex: `log_${i}_date`,
        });
    }

    // Build table data - one row per project
    const tableData: any[] = [];

    for (const project of projects) {
        const row: any = {};

        // Add project data
        for (const col of projectColumns) {
            let value = project[col.dataIndex];

            // Format dates
            if (col.dataIndex.toLowerCase().includes('date') || col.dataIndex === 'createdAt') {
                value = value ? moment(value).format('DD.MM.YYYY HH:mm') : '';
            }

            if (col.dataIndex === 'client') {
                let clientData = '';

                if (project.clientName) {
                    clientData += project.clientName;
                }

                if (project.clientPhone) {
                    if (clientData) {
                        clientData += ' | ';
                    }
                    clientData += project.clientPhone;
                }

                if (project.clientStreetNameAndNumber) {
                    if (clientData) {
                        clientData += ' | ';
                    }
                    clientData += project.clientStreetNameAndNumber;
                }

                if (project.clientPoNumber) {
                    if (clientData) {
                        clientData += ' | ';
                    }
                    clientData += project.clientPoNumber;
                }

                if (project.clientCity) {
                    if (clientData) {
                        clientData += ' | ';
                    }
                    clientData += project.clientCity;
                }

                value = clientData;
            }

            row[col.dataIndex] = value ?? '';
        }

        // Add internal orders data
        const internalOrders = project.projectInternalOrders || [];
        for (let i = 0; i < maxInternalOrders; i++) {
            if (i < internalOrders.length) {
                const order = internalOrders[i];
                // Format: "OrderNumber | System | ManufactureDate"
                const orderInfo = [
                    order.orderNumber || '',
                    order.system || '',
                    order.manufactureDate ? moment(order.manufactureDate).format('DD.MM.YYYY') : '',
                ]
                    .filter(Boolean)
                    .join(' | ');
                row[`internalOrder_${i}`] = orderInfo;
            } else {
                row[`internalOrder_${i}`] = '';
            }
        }

        // Add logs data
        const logs = (project.projectLogs || []).filter(
            (log: any) => log.changeType === 'STATUS_CHANGE' || log.changeType === 'PROJECT_CREATE',
        );

        for (let i = 0; i < maxLogs; i++) {
            if (i < logs.length) {
                const log = logs[i];
                row[`log_${i}_message`] = log.message || '';
                row[`log_${i}_date`] = log.createdAt ? moment(log.createdAt).format('DD.MM.YYYY HH:mm') : '';
            } else {
                row[`log_${i}_message`] = '';
                row[`log_${i}_date`] = '';
            }
        }

        tableData.push(row);
    }

    downloadTableAsExcel(combinedColumns, tableData, 'extended-projects-' + entityType);
}

export async function prepareAndDownloadExcel(entityType: string, currentUserId: string, fromFilter?: string) {
    let query!: string;
    let localStorageParameters!: FilterUrl;
    let currentPageNumber!: number;

    let columnOptions = leadsColumnData;

    switch (entityType) {
        case ProjectTypes.Order:
            columnOptions = ordersColumnData;
            break;
        case ProjectTypes.Offer:
            columnOptions = offersColumnData;
            break;
        case ProjectTypes.Admin:
            columnOptions = adminColumnData;
            break;
        case ProjectTypes.Request:
            columnOptions = requestColumnData;
            break;
        case ProjectTypes.All:
            columnOptions = allColumnData;
    }

    try {
        const searchParameters = constructSearchParametersFromLocalStorage(entityType, currentUserId, 10000);
        query = searchParameters.query;

        if (fromFilter) {
            query += '&includeAdditional=' + fromFilter;
        }

        localStorageParameters = searchParameters.localStorageParameters;
        currentPageNumber = searchParameters.currentPageNumber;

        const projects = await fetchAndReturnActivitiesOrNextStepsBasedOnParameters({
            parameters: localStorageParameters,
            query,
        });

        const excelData = formatExcelDataForExport(projects, columnOptions);
        downloadExcelFile(excelData, `${i18n.t('Roltek projects')}`, [], false);
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        return Promise.reject();
    }
}

function formatExcelDataForExport(projects: any[], columns: ColumnOptions[]) {
    const datePickerLocale = getDatepickerLocale();
    const headerRow: string[] = [];
    const clientColumnIndex = columns.findIndex((col) => col.name === AppTableColumn.client);

    columns.forEach((col, index) => {
        headerRow.push(i18n.t(col.name) as string);
        if (index === clientColumnIndex) {
            headerRow.push(i18n.t('Email') as string);
        }
    });

    const dataRows = [];

    for (const project of projects) {
        const row: string[] = [];

        columns.forEach((col, index) => {
            const keyPath = col?.printName ?? col.key.split('.');
            let value;

            if (typeof keyPath === 'string') {
                value = project?.[keyPath];
            } else {
                for (const key of keyPath) {
                    value = project?.[key];
                    if (value === undefined || value === null) break;
                }
            }

            // Format dates
            if (col.type === 'date' && value) {
                value = moment(value).format(datePickerLocale.lang.dateFormat) ?? '-'; // Adjust toLocaleString if needed
            }

            // Handle arrays (like productsInfo)
            if (Array.isArray(value)) {
                value = value.length > 0 ? `(${value.length}) items` : '';
            }

            // Fallback for null/undefined
            if (value === undefined || value === null) {
                value = '';
            }

            row.push(value);

            if (index === clientColumnIndex) {
                row.push(project?.clientEmail ?? '');
            }
        });

        dataRows.push(row);
    }

    return [headerRow, ...dataRows];
}

export async function fetchAndReturnActivitiesOrNextStepsBasedOnParameters({
    parameters,
    query,
}: FetchTableEntitiesConfiguration) {
    let projects;

    try {
        if (parameters.entityType === ProjectTypes.Request) {
            const requestProjects = await Project.getAndReturnFilteredProjects(query, parameters.entityType);

            projects = requestProjects.map((project: any) => {
                const latestOffer = project.offerRevisions?.[0];
                const client = project.client || {};
                const address = client.address || {};
                const assignedUser = project.assignedUser || {};
                const madeByUser = project.madeByUser || {};
                const label = project.label || {};

                return {
                    typeSpecificId: project.id,
                    userGroupId: project.userGroupId,
                    priceType: client.priceType?.name || '',
                    priceListRegion: client.priceListRegion?.name || '',
                    madeBy: madeByUser.name || '',
                    assignedUser: assignedUser.name || '',
                    state: label.name || '',
                    connection: project.connection || '',
                    createdAt: project.sortDate,
                    client: client.name || '',
                    country: address.country?.name || '',
                };
            });
        } else {
            projects = await ProjectSearch.getAndReturnAll(query);
        }
    } catch (e) {
        throw e;
    }

    return projects;
}
// Fetch few latest activities projects
export async function fetchActivitiesProjects(currentUserId: string) {
    const searchParameters = `&q=project.temporary:0^(assignedUser.id:${currentUserId})&sort=-dateFrom&page[number]=1&page[size]=3`;

    try {
        await NextStep.getFilteredProjects(searchParameters);
    } catch (e) {
        notification.error({
            message: i18n.t('Error happened') as string,
            description: (e as Error).message,
        });
        return Promise.reject();
    }

    await Vue.nextTick();

    return Promise.resolve();
}

export function printTable({ printFunction, elementId, entityType, onPrintFinish }: PrintTableConfigurator) {
    printFunction(
        elementId,
        { windowTitle: i18n.t('Roltek projects') + ' - ' + entityType, styles: ['/printTableStyle.css'] },
        onPrintFinish,
    );
}

export function generateSearchMessage(numberOfTotalItems: number) {
    if (numberOfTotalItems <= 0) {
        return i18n.t('Nije pronađen niti jedan rezultat');
    }

    if (numberOfTotalItems === 1) {
        return i18n.t('Pronađen jedan rezultat');
    }

    return (i18n.t(`Pronađeno je ukupno __NUMBEROFTOTALITEMS__ rezultata`) as string).replace(
        '__NUMBEROFTOTALITEMS__',
        String(numberOfTotalItems),
    );
}

function extractCreatedAtDateFromOrderedOffer(
    array: Offer[],
    columnOptionType: string,
    dateFormat: string,
    propertyToBeDisplayed: string,
) {
    const orderedOffer = array.filter((offer) => offer.state === OfferStates.ORDERED);
    const importedOffer = array.filter((offer) => offer.state === OfferStates.IMPORTED);

    const offerToBeUsed = orderedOffer.length > 0 ? orderedOffer : importedOffer;
    const valueToBeDisplayed = offerToBeUsed.length > 0 ? offerToBeUsed[0][propertyToBeDisplayed] : null;

    return convertValueToDateIfNecessary(valueToBeDisplayed, columnOptionType, dateFormat);
}

export const ordersColumnData: ColumnOptions[] = [
    {
        key: 'orderNumber',
        name: AppTableColumn.title,
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: false,
        shouldBreakNone: true,
        slotName: 'orderNumber',
    },
    {
        key: 'clientName',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'orderCreatedAt',
        name: AppTableColumn.createdAt,
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    // {
    //     key: 'manufactureDate',
    //     name: AppTableColumn.manufactureDate,
    //     shouldSort: true,
    //     shouldFilter: false,
    //     type: 'date',
    //     badge: false,
    // },
    {
        key: 'orderStatusId',
        name: AppTableColumn.state,
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'status',
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'price',
        name: AppTableColumn.price,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
    {
        key: 'connection',
        name: AppTableColumn.connection,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        ellipsis: true,
        isNarrowWidth: true,
    },
    {
        key: 'productsInfo',
        name: 'Internal orders',
        shouldSort: false,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'expand',
    },
];

export const offersColumnData: ColumnOptions[] = [
    {
        key: 'offerName', //
        name: AppTableColumn.title,
        shouldSort: true,
        shouldFilter: false,
        type: 'string', // array last
        sortBy: 'offerCreatedAt',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'offerCreatedAt',
        name: AppTableColumn.createdAt,
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    {
        key: 'orderStatusId',
        name: AppTableColumn.state,
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'status',
    },
    {
        key: 'clientName',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'price',
        name: AppTableColumn.price,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
    {
        key: 'connection',
        name: AppTableColumn.connection,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        ellipsis: true,
        isNarrowWidth: true,
    },
];

export const requestColumnData: ColumnOptions[] = [
    {
        key: 'typeSpecificId',
        name: AppTableColumn.title,
        printName: 'typeSpecificId',
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        sortBy: 'createdAt',
        badge: false,
        shouldBreakNone: true,
        slotName: 'typeSpecificId',
    },
    {
        key: 'userGroupId',
        name: AppTableColumn.userGroup,
        printName: 'userGroupId',
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'client.priceType.name',
        name: AppTableColumn.priceType,
        printName: 'priceType',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'client.priceListRegion.name',
        name: AppTableColumn.priceListRegion,
        printName: 'priceListRegion',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'madeByUser.name',
        name: AppTableColumn.madeBy,
        printName: 'madeBy',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'assignedUser.name',
        name: AppTableColumn.assignedUser,
        printName: 'assignedUser',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'label.name',
        name: AppTableColumn.state,
        printName: 'state',
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'status',
    },
    {
        key: 'connection',
        name: AppTableColumn.connection,
        printName: 'connection',
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        ellipsis: true,
        isNarrowWidth: true,
    },
    {
        key: 'sortDate',
        name: AppTableColumn.createdAt,
        printName: 'createdAt',
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    {
        key: 'client.name',
        name: AppTableColumn.client,
        printName: 'client',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'client.address.country.name',
        name: AppTableColumn.country,
        printName: 'country',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        isNarrowWidth: true,
    },
];

export const preorderReviewColumnData: ColumnOptions[] = [
    {
        key: 'clientName',
        name: AppTableColumn.client,
        printName: 'clientName',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        printName: 'assignedUserName',
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
    {
        key: 'preorderHasPositions',
        name: AppTableColumn.preorderStatus,
        printName: 'preorderHasPositions',
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: false,
        slotName: 'preorderStatusBadge',
    },
    {
        key: 'connection',
        name: AppTableColumn.connection,
        printName: 'connection',
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        ellipsis: true,
        isNarrowWidth: true,
    },
    {
        key: 'preorderCreatedAt',
        name: AppTableColumn.createdAt,
        printName: 'preorderCreatedAt',
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested date',
        badge: false,
    },
    {
        key: 'clientCountryName',
        name: AppTableColumn.preorderCountry,
        printName: 'clientCountryName',
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'preorderReviewAction',
        name: AppTableColumn.actions,
        printName: 'preorderReviewAction',
        shouldSort: false,
        shouldFilter: false,
        type: 'editable',
        badge: false,
        slotName: 'preorderReviewAction',
    },
];

export const leadsColumnData: ColumnOptions[] = [
    {
        key: 'clientName',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'createdAt',
        name: AppTableColumn.createdAt,
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    {
        key: 'orderStatusId',
        name: AppTableColumn.state,
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'status',
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'leadNumberOfProducts',
        name: AppTableColumn.numberOfProducts,
        shouldSort: true,
        shouldFilter: false,
        type: 'number nested',
        badge: false,
    },
];

export const adminColumnData: ColumnOptions[] = [
    {
        key: 'clientName',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'clientCity',
        name: AppTableColumn.city,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'createdAt',
        name: AppTableColumn.createdAt,
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    {
        key: 'contactPersonPhone',
        name: AppTableColumn.phone,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        shouldBreakAll: true,
    },
    {
        key: 'leadNumberOfProducts',
        name: AppTableColumn.numberOfProducts,
        shouldSort: true,
        shouldFilter: false,
        type: 'number nested',
        badge: false,
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        shouldSort: false,
        shouldFilter: false,
        type: 'editable',
        badge: false,
        slotName: 'name',
    },
];

export const allColumnData: ColumnOptions[] = [
    {
        key: 'typeSpecificId',
        name: AppTableColumn.title,
        shouldSort: false,
        shouldFilter: false,
        type: 'editable',
        sortBy: 'createdAt',
        badge: false,
        shouldBreakNone: true,
        slotName: 'typeSpecificId',
    },
    {
        key: 'clientName',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'createdAt',
        name: AppTableColumn.createdAt,
        shouldSort: true,
        shouldFilter: false,
        type: 'date',
        badge: false,
    },
    {
        key: 'orderStatusId',
        name: AppTableColumn.state,
        shouldSort: true,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        isNarrowWidth: true,
        slotName: 'status',
    },
    {
        key: 'assignedUserName',
        name: AppTableColumn.assignedUser,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'price', // 'selectedOffer.price',
        name: AppTableColumn.price,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
    {
        key: 'connection',
        name: AppTableColumn.connection,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
        ellipsis: true,
        isNarrowWidth: true,
    },
    {
        key: 'productsInfo',
        name: 'Internal orders',
        shouldSort: false,
        shouldFilter: false,
        type: 'editable',
        badge: true,
        slotName: 'expand',
    },
];

export const clientColumnData: ColumnOptions[] = [
    {
        key: 'id',
        name: AppTableColumn.title,
        shouldSort: true,
        shouldFilter: false,
        type: 'number',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'name',
        name: AppTableColumn.client,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested contact',
        badge: false,
        shouldBreakNone: true,
    },
    {
        key: 'address.streetNameAndNumber',
        name: AppTableColumn.address,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'address.poNumber',
        name: AppTableColumn.postalCode,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'address.city',
        name: AppTableColumn.city,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
    },
    {
        key: 'address.country.name',
        name: AppTableColumn.country,
        shouldSort: true,
        shouldFilter: false,
        type: 'string nested',
        badge: false,
        isNarrowWidth: true,
    },
    {
        key: 'taxId',
        name: AppTableColumn.taxId,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
    {
        key: 'visibleId',
        name: AppTableColumn.ingeniousId,
        shouldSort: true,
        shouldFilter: false,
        type: 'string',
        badge: false,
    },
];
